import { Chip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-premium';
import type { TFunction } from 'i18next';
import { getDateShortcutFilterOperators } from '@/lib/dateGridFilters';
import { formatDateTime } from '@/lib/locale';
import { getStringFilterOperators } from '@/lib/stringGridFilters';
import { UserAccountTypeChip } from '@/core/UserAccountTypeChip';
import { parseUserAccountType } from '@/core/userAccountType';

/** An ISO timestamp from the API as the Date a dateTime column sorts, filters and formats on. */
function toDate(value: unknown): Date | null {
  if (!value) return null;
  const date = new Date(value as string);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value: unknown): string {
  return value instanceof Date ? formatDateTime(value) : '';
}

/**
 * The Users grid's columns. Every filterable column has a matching value/operator pair on the
 * server (GetUserListInput) and every sortable one is on its sort whitelist; the account type is
 * derived from the account's markers, so it is shown but neither sorted nor filtered.
 */
export function buildUserListColumns(t: TFunction): GridColDef[] {
  return [
    {
      field: 'userName',
      headerName: t('identity.username'),
      width: 180,
      filterOperators: getStringFilterOperators(),
    },
    {
      field: 'email',
      headerName: t('identity.email'),
      width: 240,
      filterOperators: getStringFilterOperators(),
    },
    {
      field: 'name',
      headerName: t('identity.firstName'),
      width: 140,
      filterOperators: getStringFilterOperators(),
    },
    {
      field: 'surname',
      headerName: t('identity.lastName'),
      width: 140,
      filterOperators: getStringFilterOperators(),
    },
    {
      field: 'accountType',
      headerName: t('identity.accountTypeColumn'),
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params) => {
        const accountType = parseUserAccountType(params.value as string | undefined);
        return accountType ? <UserAccountTypeChip accountType={accountType} /> : null;
      },
    },
    {
      field: 'isActive',
      headerName: t('common.status'),
      type: 'boolean',
      width: 100,
      renderCell: (params) =>
        params.value ? (
          <Chip label={t('common.active')} size="small" color="success" />
        ) : (
          <Chip label={t('common.inactive')} size="small" color="default" />
        ),
    },
    {
      field: 'lockoutEnd',
      headerName: t('identity.locked'),
      width: 100,
      filterable: false,
      renderCell: (params) => {
        const lockedOut = params.value && new Date(params.value as string) > new Date();
        return lockedOut ? <Chip label={t('identity.locked')} size="small" color="error" /> : null;
      },
    },
    {
      field: 'lastSignInTime',
      headerName: t('identity.lastSignIn'),
      type: 'dateTime',
      width: 180,
      valueGetter: (value) => toDate(value),
      valueFormatter: (value) => formatDate(value),
      filterOperators: getDateShortcutFilterOperators(true),
    },
    {
      field: 'creationTime',
      headerName: t('grid.created'),
      type: 'dateTime',
      width: 180,
      valueGetter: (value) => toDate(value),
      valueFormatter: (value) => formatDate(value),
      filterOperators: getDateShortcutFilterOperators(true),
    },
  ];
}
